import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { finalize, map } from "rxjs/operators";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { NameInputDialogComponent } from "../../shared/name-input-dialog/name-input-dialog.component";
import { BackgroundService } from "../background.service";
import { DigitalCardService } from "../../digital-cards/digital-card.service";
import type { DigitalCardProfileItem } from "../../digital-cards/digital-cards.interface";
import { CompanyService } from "../../company/company.service";
import type { EmployeePickerItem } from "../../employees/employees.interface";
import type { BackgroundForm } from "../backgrounds.interface";
import {
  BRAND_CANVAS_FONT_PRESETS,
  BRAND_FONT_PRESET_ID,
} from "../../shared/font-presets";
import type { BrandFontPreset } from "../../shared/font-presets.interface";

@Component({
  selector: "app-background-create",
  templateUrl: "./background-create.component.html",
  styleUrls: ["./background-create.component.scss"],
  standalone: false,
})
export class BackgroundCreateComponent implements OnInit, OnDestroy {
  loading = true;
  saving = false;
  bannerPreviewUrl: string | null = null;
  companyLogoUrl: string | null = null;
  companyFontFamily = "";
  companyFontFileUrl: string | null = null;
  previewQrTarget: string | null = null;
  loadingPreview = false;
  employeePickerReady = false;
  digitalCardTemplates: DigitalCardProfileItem[] = [];
  readonly digitalCardTemplateLabel = (template: DigitalCardProfileItem) =>
    template.displayName || template.profileId || "";
  readonly digitalCardTemplateValue = (template: DigitalCardProfileItem) =>
    template.profileId;
  previewEmployee: EmployeePickerItem | null = null;
  private readonly builtInFontPresets = BRAND_CANVAS_FONT_PRESETS;
  private previewQrSub?: Subscription;

  form = new FormGroup<BackgroundForm>({
    name: new FormControl<string>("", {
      nonNullable: true,
    }),
    bannerType: new FormControl<"gradient" | "image">("gradient", {
      nonNullable: true,
    }),
    bannerGradientStart: new FormControl<string>("#ffffff", {
      nonNullable: true,
    }),
    bannerGradientEnd: new FormControl<string>("#f5f5f0", {
      nonNullable: true,
    }),
    qrLabel: new FormControl<string>("", { nonNullable: true }),
    showLogo: new FormControl<boolean>(true, { nonNullable: true }),
    digitalCardProfileId: new FormControl<string | null>(null),
    fontPreset: new FormControl<string>("default", { nonNullable: true }),
  });

  constructor(
    private router: Router,
    private backgroundService: BackgroundService,
    private digitalCardService: DigitalCardService,
    private companyService: CompanyService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.companyService.get().subscribe({
      next: (company) => {
        this.companyLogoUrl = company?.photoUrl
          ? `${company.photoUrl}?t=${Date.now()}`
          : null;
        this.companyFontFamily =
          (company?.brandFontFamily ?? "").trim() ||
          (company?.brandFontFileName ?? "").trim().replace(/\.[^.]+$/, "") ||
          "";
        this.companyFontFileUrl =
          (company?.brandFontFileUrl ?? "").trim() || null;
        if (this.companyBrandFontReady) {
          this.form.patchValue(
            { fontPreset: BRAND_FONT_PRESET_ID },
            { emitEvent: false },
          );
        }
      },
    });
    this.digitalCardService.getProfiles({ templatesOnly: true }).subscribe({
      next: (res) => {
        this.digitalCardTemplates = res.profiles ?? [];
        if (!this.form.get("digitalCardProfileId")?.value) {
          this.employeePickerReady = true;
          this.loadPreviewQrTarget();
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.previewQrSub?.unsubscribe();
  }

  get pageReady(): boolean {
    return !this.loading;
  }

  get canSave(): boolean {
    return true;
  }

  get companyBrandFontReady(): boolean {
    return (
      this.companyFontFamily.trim().length > 0 &&
      (this.companyFontFileUrl?.trim().length ?? 0) > 0
    );
  }

  get fontPresets(): BrandFontPreset[] {
    if (!this.companyBrandFontReady) {
      return this.builtInFontPresets;
    }
    return [
      {
        id: BRAND_FONT_PRESET_ID,
        label: this.companyFontFamily.trim(),
        fontFamily: `"${this.companyFontFamily.replace(/"/g, "")}"`,
      },
      ...this.builtInFontPresets,
    ];
  }

  get selectedFont(): BrandFontPreset {
    return (
      this.fontPresets.find(
        (font) => font.id === this.form.controls.fontPreset.value,
      ) ?? this.fontPresets[0]
    );
  }

  get previewFontFamily(): string {
    return this.selectedFont.fontFamily;
  }

  get previewFontCssUrl(): string | null {
    return this.selectedFont.id === BRAND_FONT_PRESET_ID
      ? null
      : (this.selectedFont.fontCssUrl ?? null);
  }

  get previewCompanyFontFileUrl(): string | null {
    return this.selectedFont.id === BRAND_FONT_PRESET_ID
      ? this.companyFontFileUrl
      : null;
  }

  get pickerTemplateContextId(): string | null {
    const digitalCardProfileId =
      this.form.get("digitalCardProfileId")?.value?.trim() ?? "";
    return digitalCardProfileId.length > 0 ? digitalCardProfileId : null;
  }

  onPreviewEmployeeChange(emp: EmployeePickerItem | null): void {
    this.previewEmployee = emp;
    this.employeePickerReady = true;
    this.loadPreviewQrTarget();
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    this.loadPreviewQrTarget();
  }

  onGradientColorChange(
    controlName: "bannerGradientStart" | "bannerGradientEnd",
    value: string,
  ): void {
    this.form.patchValue({ [controlName]: value });
  }

  save(): void {
    if (this.saving) {
      return;
    }
    if (!this.form.get("name")?.value.trim()) {
      this.showNameInputDialog();
      return;
    }
    this.performSave();
  }

  private showNameInputDialog(): void {
    const dialogRef = this.dialog.open(NameInputDialogComponent, {
      width: "500px",
      data: {
        title: this.translate.instant("background.enterName") as string,
        placeholder: this.translate.instant("footers.name") as string,
        currentName: this.form.get("name")?.value || "",
        actionType: "save" as const,
        saveButtonText: this.translate.instant("save") as string,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe((result: { name?: string; action?: string } | undefined) => {
        if (!result?.name?.trim()) {
          return;
        }
        this.form.get("name")?.setValue(result.name.trim());
        this.performSave();
      });
  }

  private performSave(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    const selected = this.selectedFont;
    const payload = {
      name: this.form.get("name")?.value.trim() || "",
      digitalCardProfileId:
        this.form.get("digitalCardProfileId")?.value || null,
      settings: {
        bannerType: this.form.get("bannerType")?.value ?? "gradient",
        bannerGradientStart:
          this.form.get("bannerGradientStart")?.value || null,
        bannerGradientEnd: this.form.get("bannerGradientEnd")?.value || null,
        qrLabel: this.form.get("qrLabel")?.value || null,
        showLogo: this.form.get("showLogo")?.value ?? true,
        bannerImageUrl:
          this.form.get("bannerType")?.value === "image"
            ? this.bannerPreviewUrl
            : null,
        fontName: selected.label,
        fontFamily: selected.fontFamily,
        fontCssUrl:
          selected.id === BRAND_FONT_PRESET_ID
            ? null
            : (selected.fontCssUrl ?? null),
      },
    };
    this.backgroundService
      .create(payload)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant("background.saved") as string,
          );
          void this.router.navigate(["/backgrounds"]);
        },
      });
  }

  back(): void {
    void this.router.navigate(["/backgrounds"]);
  }

  uploadBanner = (file: File) =>
    this.backgroundService
      .uploadImage(file)
      .pipe(map((res) => res.bannerImageUrl));

  onBannerFileChange(image: { previewUrl?: string }): void {
    this.bannerPreviewUrl = image.previewUrl ?? null;
    this.form.patchValue({ bannerType: "image" });
  }

  removeBanner(): void {
    this.bannerPreviewUrl = null;
  }

  onDigitalCardProfileChange(): void {
    this.employeePickerReady = false;
    this.previewEmployee = null;
    this.previewQrTarget = null;
    this.loadingPreview = true;
  }

  private loadPreviewQrTarget(): void {
    const profileId =
      this.form.get("digitalCardProfileId")?.value?.trim() || null;
    if (!profileId) {
      this.previewQrTarget =
        globalThis.location?.origin || "https://gsignature.com";
      this.loadingPreview = false;
      return;
    }
    if (!this.employeePickerReady) {
      this.loadingPreview = true;
      return;
    }
    this.loadingPreview = true;
    this.previewQrSub?.unsubscribe();
    const previewEmail = this.previewEmployee?.email?.trim() || null;
    this.previewQrSub = this.backgroundService
      .getPreviewQrTarget(profileId, false, previewEmail)
      .subscribe({
        next: (res) => {
          this.previewQrTarget = res.qrTarget || null;
          this.loadingPreview = false;
        },
        error: () => {
          this.previewQrTarget =
            globalThis.location?.origin || "https://gsignature.com";
          this.loadingPreview = false;
        },
      });
  }
}
